/**
 * 延長料金の階段計算ユーティリティ
 *
 * ビジネスルール（顧客要望①）:
 * - 1時間あたり ¥1,500
 * - 0-15分以内: ¥0
 * - 16-30分以内: ¥750
 * - 31-60分以内: ¥1,500
 * - 1時間超は上記サイクルを繰り返す
 */

export const DEFAULT_HOURLY_RATE = 1500;
export const DEFAULT_GRACE_MINUTES = 5;

/** 階段ティア: maxMinutes (inclusive) と feeRatio (hourlyRate に対する比率) */
export interface ExtensionTier {
  maxMinutes: number;
  feeRatio: number;
}

export const DEFAULT_TIERS: ExtensionTier[] = [
  { maxMinutes: 15, feeRatio: 0.0 },
  { maxMinutes: 30, feeRatio: 0.5 },
  { maxMinutes: 60, feeRatio: 1.0 },
];

export interface ExtensionFeeBreakdown {
  fullHours: number;
  remainderMinutes: number;
  remainderFee: number;
  totalFee: number;
  hourlyRate: number;
}

export function calculateTieredExtensionFee(
  overtimeMinutes: number,
  hourlyRate: number = DEFAULT_HOURLY_RATE,
  tiers: ExtensionTier[] = DEFAULT_TIERS
): number {
  if (overtimeMinutes <= 0) return 0;
  const tierList = tiers && tiers.length > 0 ? tiers : DEFAULT_TIERS;
  const fullHours = Math.floor(overtimeMinutes / 60);
  const rem = overtimeMinutes % 60;
  let remCharge = hourlyRate;
  for (const t of tierList) {
    if (rem <= t.maxMinutes) {
      remCharge = hourlyRate * t.feeRatio;
      break;
    }
  }
  return fullHours * hourlyRate + remCharge;
}

export function extensionFeeBreakdown(
  overtimeMinutes: number,
  hourlyRate: number = DEFAULT_HOURLY_RATE,
  tiers: ExtensionTier[] = DEFAULT_TIERS
): ExtensionFeeBreakdown {
  if (overtimeMinutes <= 0) {
    return { fullHours: 0, remainderMinutes: 0, remainderFee: 0, totalFee: 0, hourlyRate };
  }
  const tierList = tiers && tiers.length > 0 ? tiers : DEFAULT_TIERS;
  const fullHours = Math.floor(overtimeMinutes / 60);
  const rem = overtimeMinutes % 60;
  let remCharge = hourlyRate;
  for (const t of tierList) {
    if (rem <= t.maxMinutes) {
      remCharge = hourlyRate * t.feeRatio;
      break;
    }
  }
  return {
    fullHours,
    remainderMinutes: rem,
    remainderFee: remCharge,
    totalFee: fullHours * hourlyRate + remCharge,
    hourlyRate,
  };
}

/**
 * PlanOrderから実際の延長料金を計算する。
 * - billingMode='tiered': 経過時間（endTime からの超過 - graceMinutes）で階段計算
 * - billingMode='fixed' (or undefined): 保存済み totalExtensionPrice を使用
 */
export interface PlanOrderLike {
  endTime?: Date | { toDate?: () => Date } | any;
  totalExtensionPrice?: number;
  checkoutAdjustment?: {
    adjustedExtensionPrice?: number;
    adjustedOvertimeMinutes?: number;
  } | null;
  extensionSettings?: {
    billingMode?: string;
    hourlyRate?: number;
    graceMinutes?: number;
    tiers?: ExtensionTier[];
  } | null;
}

function toDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function computeEffectiveExtensionPrice(
  plan: PlanOrderLike,
  referenceTime: Date = new Date()
): number {
  // 会計時調整があれば最優先
  if (plan.checkoutAdjustment?.adjustedExtensionPrice != null) {
    return plan.checkoutAdjustment.adjustedExtensionPrice;
  }
  const settings = plan.extensionSettings;
  const isTiered = (settings?.billingMode ?? 'tiered') === 'tiered';
  if (!isTiered) {
    return plan.totalExtensionPrice ?? 0;
  }
  const endTime = toDate(plan.endTime);
  if (!endTime) return plan.totalExtensionPrice ?? 0;
  const grace = settings?.graceMinutes ?? DEFAULT_GRACE_MINUTES;
  const overtime = calculateOvertimeMinutes(endTime, referenceTime);
  const chargeable = overtime > grace ? overtime - grace : 0;
  return calculateTieredExtensionFee(
    chargeable,
    settings?.hourlyRate ?? DEFAULT_HOURLY_RATE,
    settings?.tiers ?? DEFAULT_TIERS
  );
}

export function describeExtensionBreakdown(b: ExtensionFeeBreakdown): string {
  const parts: string[] = [];
  if (b.fullHours > 0) {
    parts.push(`${b.fullHours}時間 ¥${(b.fullHours * b.hourlyRate).toLocaleString()}`);
  }
  if (b.remainderMinutes > 0) {
    let label: string;
    if (b.remainderFee === 0) label = '無料';
    else if (b.remainderFee < b.hourlyRate) label = '半額';
    else label = '全額';
    parts.push(`${b.remainderMinutes}分(${label}) ¥${b.remainderFee.toLocaleString()}`);
  }
  if (parts.length === 0) return '¥0';
  return `${parts.join(' + ')} = ¥${b.totalFee.toLocaleString()}`;
}

/**
 * 経過時間（分）= 現在時刻 or 会計時刻 - プラン終了時刻
 * 負数（プラン終了前）の場合は 0。
 */
export function calculateOvertimeMinutes(planEndTime: Date, referenceTime: Date = new Date()): number {
  const diffMs = referenceTime.getTime() - planEndTime.getTime();
  if (diffMs <= 0) return 0;
  return Math.floor(diffMs / 60000);
}
